// src/services/ReverseSearchService.mjs
/**
 * Reverse asset search — find assets by CONTENT rather than by metadata.
 *
 * Phase 1 exposes the cheapest entry point: "assets like this one", where both
 * operands are already in the index so the request costs no upload and no
 * decode. Query-by-uploaded-file lands in Phase 2 and will sit alongside these.
 *
 * Every call goes through `apiClient`, so it inherits the auth token, the
 * workspace header and the single-reporter error handling; no `catch` here
 * re-reports anything the interceptor already surfaced.
 */
import apiClient from '../config/axiosConfig.js';

/**
 * Assets most similar to `fileId`, ranked, banded and with the evidence behind
 * each match.
 *
 * `options` is forwarded to axios so a caller that renders its own inline error
 * (rather than a global toast) can pass `{ skipGlobalErrorToast: true }` —
 * which the similar-assets panel does, because a failure there is a section of
 * a modal being empty, not something worth interrupting the user over.
 */
export async function findSimilar(fileId, { limit, ...options } = {}) {
    const response = await apiClient.post(
        '/api/search/similar',
        { fileId, ...(limit ? { limit } : {}) },
        options,
    );
    return response.data;
}

/**
 * Search the library with a file from the user's machine.
 *
 * Three steps, deliberately visible here rather than hidden behind one call, so
 * the progress callback can distinguish "still uploading your 40 MB texture"
 * from "searching" — the two have very different expected durations and a
 * single spinner for both reads as a hang.
 *
 *   1. `init`  — mint a scratch upload URL. No file is created and no storage
 *                quota is touched; see the backend's `queryService`.
 *   2. PUT     — straight to GCS, bypassing the API entirely.
 *   3. `run`   — extract, search, verify. The bytes are deleted server-side the
 *                moment their descriptors exist.
 */
export async function searchByFile(file, { partial = false, onStage, signal } = {}) {
    onStage?.('preparing');
    const init = await apiClient.post('/api/search/reverse/init', {
        name: file.name,
        bytes: file.size,
        mimetype: file.type || 'application/octet-stream',
        partial,
    }, { signal });

    const { queryId, uploadUrl } = init.data;

    onStage?.('uploading', 0);
    await putToSignedUrl(uploadUrl, file, {
        signal,
        onProgress: (loaded, total) => onStage?.('uploading', total ? loaded / total : 0),
    });

    onStage?.('searching');
    const run = await apiClient.post('/api/search/reverse/run', { queryId }, { signal });
    return run.data;
}

/**
 * A single PUT to a signed URL, with upload progress.
 *
 * Hand-rolled on XHR rather than reusing `resumableUpload.uploadBytes`, for two
 * reasons:
 *
 *  - **It doesn't need it.** That module exists for multi-gigabyte resumable
 *    sessions; a query file is capped at a couple of hundred megabytes and is
 *    always one PUT. Importing it pulled `axios` — and its untransformed ESM
 *    build — into every component that touches reverse search, which broke
 *    unrelated test suites the moment the "Similar" tab was added to the file
 *    details modal.
 *  - **`fetch` cannot report upload progress.** XHR can, and progress is the
 *    difference between "sending your 40 MB texture" and an apparent hang.
 *
 * Deliberately does NOT go through `apiClient`: a signed URL carries its own
 * authorization, and attaching a Firebase bearer token to a GCS request is at
 * best ignored and at worst rejected.
 */
function putToSignedUrl(url, file, { signal, onProgress } = {}) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('PUT', url, true);
        request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

        request.upload.onprogress = (event) => {
            if (onProgress) onProgress(event.loaded, event.total || file.size);
        };
        request.onload = () => {
            if (request.status >= 200 && request.status < 300) resolve();
            else reject(new Error(`Upload failed (${request.status})`));
        };
        request.onerror = () => reject(new Error('Upload failed'));
        request.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

        if (signal) {
            if (signal.aborted) { request.abort(); return; }
            signal.addEventListener('abort', () => request.abort(), { once: true });
        }
        request.send(file);
    });
}

/** Poll a search that is still running. */
export async function getQuery(queryId, options = {}) {
    const response = await apiClient.get(
        `/api/search/reverse/query/${encodeURIComponent(queryId)}`, options,
    );
    return response.data;
}

/**
 * What this deployment can index, and whether the vector indexes are live.
 *
 * Worth its own call because "your library has nothing like this", "this
 * deployment does not index 3D yet" and "the vector index is still building"
 * are three different situations that otherwise render as the same empty grid —
 * and only the first is the user's problem.
 */
export async function getCapabilities(options = {}) {
    const response = await apiClient.get('/api/search/reverse/capabilities', options);
    return response.data;
}

/** How much of the workspace is indexed, and why anything was skipped. */
export async function getCoverage(options = {}) {
    const response = await apiClient.get('/api/search/reverse/coverage', options);
    return response.data;
}

/** Force one file back through the extractors, ignoring compare-and-skip. */
export async function reindexFile(fileId, options = {}) {
    const response = await apiClient.post('/api/search/reverse/reindex', { fileId }, options);
    return response.data;
}

/**
 * Index the files in a workspace that need it.
 *
 * Defaults to a DRY RUN, mirroring the server. Returns a cursor; a large
 * library is backfilled by calling this repeatedly rather than by one request
 * that runs for minutes.
 */
export async function runBackfill({ dryRun = true, cursor = null, limit } = {}, options = {}) {
    const response = await apiClient.post(
        '/api/search/reverse/backfill',
        { dryRun, cursor, ...(limit ? { limit } : {}) },
        options,
    );
    return response.data;
}

// ── Presentation helpers ────────────────────────────────────────────────────
//
// Re-exported from `utils/reverseSearchFormat` so a component can pull the API
// call and the formatting from one place, while the formatting itself stays
// importable WITHOUT the axios client, Firebase auth and workspace header this
// module drags in. See that file's header.

export {
    BAND_LABELS, describeEvidence, describeIndexState, regionOverlay,
} from '../utils/reverseSearchFormat.js';
